import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import { MarketingError, type MarketingRepository } from '../application/MarketingRepository'
import {
  adsBreakdownRowSchema,
  ga4BreakdownRowSchema,
  gscBreakdownRowSchema,
  marketingOverviewSchema,
  marketingSourceSchema,
  marketingStatusSchema,
} from '../domain/marketing'

interface RpcResponse {
  data: unknown
  error: { message: string } | null
}

const syncResponseSchema = z.object({
  results: z.array(
    z.object({
      source: marketingSourceSchema,
      status: z.enum(['SUCCEEDED', 'FAILED', 'SKIPPED']),
      rows: z.number().optional(),
      error: z.string().optional(),
    }),
  ),
})

const testResponseSchema = z.object({
  results: z.array(
    z.object({
      source: marketingSourceSchema,
      ok: z.boolean(),
      accountFound: z.boolean(),
      errorCode: z.string().nullable(),
    }),
  ),
})

/** Code d'erreur stable d'une fonction de lecture (`MARKETING_READ_FORBIDDEN`, …), jamais le message brut. */
function rpcErrorCode(message: string): string {
  return /MARKETING_[A-Z_]+/.exec(message)?.[0] ?? 'UNEXPECTED'
}

async function functionErrorCode(error: unknown): Promise<string> {
  const context = (error as { context?: unknown } | null)?.context
  if (context instanceof Response) {
    const body = (await context.json().catch(() => null)) as { error?: unknown } | null
    if (typeof body?.error === 'string') return body.error
  }
  return 'UNEXPECTED'
}

/** Aucun repli sur des fixtures : un faux chiffre de dépenses se lirait comme un fait. */
export function createSupabaseMarketingRepository(client: AppSupabaseClient): MarketingRepository {
  const rpcClient = client as unknown as SupabaseClient

  async function rpc<T>(
    name: string,
    args: Record<string, unknown> | undefined,
    schema: z.ZodType<T>,
  ): Promise<T> {
    let response: RpcResponse
    try {
      response = await rpcClient.rpc(name, args)
    } catch {
      throw new MarketingError('NETWORK')
    }
    if (response.error) throw new MarketingError(rpcErrorCode(response.error.message))
    const parsed = schema.safeParse(response.data)
    if (!parsed.success) throw new MarketingError('RESPONSE_INVALID')
    return parsed.data
  }

  async function invoke<T>(body: object, schema: z.ZodType<T>): Promise<T> {
    let invocation: Awaited<ReturnType<typeof client.functions.invoke>>
    try {
      invocation = await client.functions.invoke('marketing-sync', { body })
    } catch {
      throw new MarketingError('NETWORK')
    }
    if (invocation.error) throw new MarketingError(await functionErrorCode(invocation.error))
    const parsed = schema.safeParse(invocation.data)
    if (!parsed.success) throw new MarketingError('RESPONSE_INVALID')
    return parsed.data
  }

  const breakdown = (source: string, kind: string, range: { from: string; to: string }) => ({
    p_source: source,
    p_kind: kind,
    p_from: range.from,
    p_to: range.to,
    p_limit: 100,
  })

  return {
    getStatus: () => rpc('marketing_status_v1', undefined, marketingStatusSchema),
    getOverview: (range) =>
      rpc('marketing_overview_v1', { p_from: range.from, p_to: range.to }, marketingOverviewSchema),
    getAdsBreakdown: (kind, range) =>
      rpc('marketing_breakdown_v1', breakdown('GOOGLE_ADS', kind, range), z.array(adsBreakdownRowSchema)),
    getGa4Breakdown: (kind, range) =>
      rpc('marketing_breakdown_v1', breakdown('GA4', kind, range), z.array(ga4BreakdownRowSchema)),
    getGscBreakdown: (kind, range) =>
      rpc('marketing_breakdown_v1', breakdown('SEARCH_CONSOLE', kind, range), z.array(gscBreakdownRowSchema)),
    async sync(sources, backfill = false) {
      const response = await invoke(
        { action: 'sync', trigger: backfill ? 'BACKFILL' : 'MANUAL', ...(sources ? { sources } : {}) },
        syncResponseSchema,
      )
      return response.results.map((result) => ({
        source: result.source,
        status: result.status,
        ...(result.rows === undefined ? {} : { rows: result.rows }),
        ...(result.error === undefined ? {} : { error: result.error }),
      }))
    },
    async testConnections() {
      return (await invoke({ action: 'test' }, testResponseSchema)).results
    },
  }
}
